import { useEffect, useRef, useState } from 'react';

import { NativeShareButton } from '@/components/site/article-share';
import { cn } from '@/lib/utils';

/** Matches GridNav inner height (`h-16`) so the bar sits flush under the site header. */
const NAV_OFFSET_PX = 64;

export function ArticleStickyTitle({
    title,
    shareUrl,
    className,
    onPinnedChange,
}: {
    title: string;
    shareUrl: string;
    className?: string;
    onPinnedChange?: (pinned: boolean) => void;
}) {
    const titleRef = useRef<HTMLHeadingElement>(null);
    const barRef = useRef<HTMLDivElement>(null);
    const [pinned, setPinned] = useState(false);

    useEffect(() => {
        const el = titleRef.current;
        if (!el || typeof IntersectionObserver === 'undefined') return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                const next = !entry.isIntersecting;
                setPinned(next);
                onPinnedChange?.(next);
                if (
                    !next
                    && barRef.current?.contains(document.activeElement)
                    && document.activeElement instanceof HTMLElement
                ) {
                    document.activeElement.blur();
                }
            },
            {
                threshold: 0,
                rootMargin: `-${NAV_OFFSET_PX}px 0px 0px 0px`,
            },
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, [onPinnedChange]);

    return (
        <>
            <h1
                id="article-title"
                ref={titleRef}
                className={cn('scroll-mt-20', className)}
            >
                {title}
            </h1>
            <div
                className={cn(
                    'pointer-events-none fixed inset-x-0 top-16 z-30 flex justify-center',
                    pinned && 'pointer-events-auto',
                )}
                aria-hidden={!pinned}
            >
                <div
                    ref={barRef}
                    className={cn(
                        'mx-auto flex h-14 w-full max-w-7xl items-center gap-3 border-x border-b border-white/10 bg-black/85 px-4 text-start backdrop-blur-md transition duration-200 motion-reduce:transition-none sm:px-6',
                        pinned
                            ? 'translate-y-0 opacity-100'
                            : 'pointer-events-none -translate-y-1.5 opacity-0',
                    )}
                >
                    <button
                        type="button"
                        tabIndex={pinned ? 0 : -1}
                        onClick={() => {
                            titleRef.current?.scrollIntoView({
                                behavior: 'smooth',
                                block: 'start',
                            });
                        }}
                        aria-label="חזרה לכותרת"
                        className="min-w-0 flex-1 self-stretch text-start"
                    >
                        <span className="block truncate text-lg tracking-tight text-white">
                            {title}
                        </span>
                    </button>
                    <NativeShareButton
                        title={title}
                        url={shareUrl}
                        iconOnly
                        tabIndex={pinned ? 0 : -1}
                        className="shrink-0 border-white/15 text-white/75"
                    />
                </div>
            </div>
        </>
    );
}
